import { parseMoneyInput } from './moneyInput.js'
// src/lib/coopMath.js — THE commission-amount projection for cooperation terms.
//
// One implementation, three consumers (2026-07-27, owner: "we need all these fields to be
// similar across the app and extension"): the CooperationModal's live "≈ საკომისიოს თანხა"
// preview, the Desk feed's commission sort key, and — by contract, not import — the
// extension's gallery calculator (deal_terms.js computes the same shapes at capture time;
// its TYPE_MAP in sentavus_wiring.js maps onto exactly these commission_type values).
// If a shape is added here, sentavus_wiring's TYPE_MAP and deal_terms must learn it too.
//
// The amount is a PROJECTION, never a stored value (0046 header: "calculator = suggestion
// layer only") — recomputed from its inputs wherever it is shown, so it can never silently
// disagree with them.
//
// Shapes (terms.commission_type):
//   percent        → price × percent / 100          (sale + rent)
//   net_amount     → price − owner's net ask         (sale: agent keeps the spread)
//   net_commission → the flat fee itself             (sale)
//   fixed_net      → the flat fee itself             (rent)
//   standard       → null — a KNOWN formula (100% of one month per 12-month lease,
//                    pro-rated) whose amount depends on lease length nobody has stated
//                    yet. Null means "not computable", NEVER zero: sorting it as 0 would
//                    rank a real agreed deal below no deal at all.
//   coop_split     → null — the percent is a SHARE OF THE COMMISSION, not of the price.
//
// ⚠️ `coop_split` exists because 50 in the percent box meant two different things (owner
// ruling 2026-09-12, "both, told apart by the commission type"). „50%-ზე ვთანამშრომლობ" is
// the commonest cooperation sentence in the corpus and it offers HALF THE FEE; read as
// `percent` it computed half the SALE PRICE as the agent's commission — on a 200,000 flat,
// a 100,000 fee, silently, in the same box that legitimately holds 3 for a 3% deal.
// It returns null for the same reason `standard` does: the amount is genuinely not
// computable from what is stored. A share of a fee needs the fee, and this cooperation
// records the split, not the listing agent's own rate. NEVER fall back to price × percent
// here "to have a number" — that is the exact arithmetic the type was added to stop.

// Strings are what an agent TYPED: read them the one way (lib/moneyInput.js) — parseFloat read
// "150 000" as 150 (review 2026-09-23).
const num = v => {
  const n = typeof v === 'string' ? parseMoneyInput(v) : v
  return typeof n === 'number' && isFinite(n) ? n : null
}

// Primitive-shaped entry — what a live form holds (CooperationModal's field states).
export function commissionAmount({ type, price, percent, ownerNet, net }) {
  const p = num(price)
  if (type === 'net_amount') {
    const o = num(ownerNet)
    return p != null && o != null ? p - o : null
  }
  if (type === 'net_commission' || type === 'fixed_net') return num(net)
  if (type === 'standard' || type === 'coop_split') return null
  const c = num(percent)
  // percent (the default when type is absent — pre-2026-07-12 rows carry no
  // commission_type and only ever wrote commission_percent)
  return p != null && c != null ? Math.round(p * c) / 100 : null
}

// Row-shaped entry — a cooperation or cooperation_participant row as the API returns it
// (commission_percent / min_price columns + the terms jsonb). Returns
// { amount, currency } | null. Works identically on parent and participant rows, and on
// PROJECTED rows: core_visibility's stripNumbers nulls the number fields for viewers not
// entitled to them, so this simply returns null there — the privacy boundary stays where
// it is, upstream, and no caller needs its own redaction awareness.
export function commissionAmountFromRow(row) {
  if (!row) return null
  const t = row.terms || {}
  const amount = commissionAmount({
    type: t.commission_type || 'percent',
    price: row.min_price,
    percent: row.commission_percent,
    ownerNet: t.owner_net_amount,
    net: t.net_commission_amount,
  })
  return amount != null ? { amount, currency: row.min_price_currency || 'USD' } : null
}

// Best amount the VIEWER can see on one cooperation: the parent's rolled-up effective
// numbers and every participant row, post-projection (own numbers survive; others' are
// nulled server-side — see core_visibility.js projectCooperation). Max, not first: with
// several visible numbers (management view), the deal's headline is its best.
export function bestVisibleCommission(coop) {
  if (!coop) return null
  let best = null
  const consider = row => {
    const r = commissionAmountFromRow(row)
    if (r && (!best || r.amount > best.amount)) best = r
  }
  consider(coop)
  for (const p of coop.participants || []) if (!p.left_at) consider(p)
  return best
}

// "Best" cooperation = the one an admin cares about first. Face cards collapse to it.
//
// ⚠️ B159: THE OLD RULE COMPARED TWO DIFFERENT UNITS. It sorted on raw
// `commission_percent`, but that number means "% of ONE MONTH'S RENT" on a rent
// cooperation (100 = one full month; live avg 80.5) and "% of the SALE PRICE" on a sale one
// (live avg 2.6). So a rent deal at 100 always beat a sale deal at 3 — ranking one month's
// rent above 3% of a sale price as though they were the same quantity. The DB now declares
// the base (`commission_basis`, core 0319); this is the read side honouring it.
//
// Not hypothetical: of the 3 properties with more than one live cooperation, TWO are
// rent+sale mixed. It has not produced a wrong face yet only because neither mixed pair has
// a percent on both sides.
//
// The rule now:
//   1. Prefer the cooperation for what THIS AD actually is — a sale card should show the
//      sale deal. This is the only honest cross-intent answer available here.
//   2. Compare percents ONLY within one base, where the comparison means something.
//   3. When the bases still differ, there is NO honest ranking — `min_price` is base-
//      dependent too (monthly rent vs sale price), so comparing it would repeat the bug one
//      column over. Fall back to "has terms recorded", then a stable id order, and rank
//      nothing by a number that cannot be compared.
export function bestCooperation(coops, preferIntent = null) {
  const hasTerms = c => (c.commission_percent != null || c.min_price != null) ? 0 : 1
  return [...(coops || [])].sort((a, b) => {
    if (preferIntent) {
      const am = a.transaction_intent === preferIntent ? 0 : 1
      const bm = b.transaction_intent === preferIntent ? 0 : 1
      if (am !== bm) return am - bm
    }
    if (a.transaction_intent === b.transaction_intent) {
      const d = (b.commission_percent ?? -1) - (a.commission_percent ?? -1)
      if (d) return d
      const m = (Number(b.min_price) || 0) - (Number(a.min_price) || 0)
      if (m) return m
    }
    return hasTerms(a) - hasTerms(b) || String(a.id || '').localeCompare(String(b.id || ''))
  })[0]
}

// ─── Plain-text terms + which cooperation speaks for a property ──────────────────────
// These two live HERE, not in CooperationChips, for one structural reason: they are pure
// (no React, no i18n) and both the meeting card and the meeting modal need them, and
// CooperationChips already imports from MeetingsFeed — importing back would close a cycle.
// Pure logic in lib, rendering in components.

// WHOSE participation speaks for a given set of parties, in PRIORITY ORDER, or null.
//
// ⚠️ THE PARENT ROW IS NOT AN ANSWER TO "WHAT ARE THIS MEETING'S TERMS". A cooperation's
// parent numbers are ROLLED UP from whichever participant most recently confirmed theirs
// (COOPERATION_VISIBILITY_SPEC; per-agent terms are participations). So a property where
// colleague X recorded 342 USD makes the parent read 342 USD for EVERYONE — and a meeting
// hosted by agent Y then displayed X's number as its own deal terms, and pasted it into
// the WhatsApp brief under Y's name (owner-caught 2026-08-01, live rows: one participant,
// Levan, on a meeting whose property representative and only attendee was Tatia).
//
// Terms belong to an agent. Ask for the ones belonging to the agents on THIS meeting; a
// miss is "not recorded yet", which is a to-do, not a number to borrow from a colleague.
export function participationOf(coop, partyIds) {
  if (!coop) return null
  const actives = (coop.participants || []).filter(p => !p.left_at)
  for (const id of (partyIds || [])) {
    if (!id) continue
    const hit = actives.find(p => p.party_id === id)
    if (hit) return hit
  }
  return null
}

// { total, commission } as plain strings, either may be null. Feeds the meeting card's
// face token, the modal's terms row, and the chat brief that gets pasted into WhatsApp —
// which is why there is no i18n, no 🔑 and no ≈ anywhere in it.
//
// ⚠️ IT HONOURS THE BLIND-FIRST GATE, and that is the load-bearing line: when the server
// withheld the numbers for this viewer, this returns nulls. A clipboard is not a loophole —
// do not drop the visibility check because the caller happens to be a copy button.
export function coopTermsPlain(c) {
  const none = { total: null, commission: null }
  if (!c) return none
  if (c.visibility && c.visibility.can_see_numbers === false) return none
  const cur = c.min_price_currency || ''
  const price = Number(c.min_price)
  const money = n => `${Number(n).toLocaleString('en-US')}${cur ? ' ' + cur : ''}`
  // A percent stays a PERCENT: the line the agents write says "how many % or a fixed
  // amount", and "50%" is what they say out loud. Every other shape has no percent to
  // state, so it falls through to the shared amount projection above rather than
  // re-deriving net_amount / net_commission / fixed_net here a second time.
  const fee = commissionAmountFromRow(c)
  const commission = c.commission_percent != null
    ? `${c.commission_percent}%`
    : fee ? `${Number(fee.amount).toLocaleString('en-US')} ${fee.currency}`.trim() : null
  return { total: price > 0 ? money(price) : null, commission }
}

// ─── Deal value: what to rank a property by, and WHICH quantity it is ─────────────────
// Measured 2026-07-27 on live rows: of 1,244 active cooperations, 1,240 carry a negotiated
// min_price but only 306 carry any commission shape — so 937 (75%) know the deal SIZE and
// not the FEE. Ranking on commission alone therefore sank three quarters of real deals into
// the undated tail, which is what the owner saw ("it sorts wrongly — bigger value first").
//
// ⚠️ The two numbers are NOT interchangeable and are never mixed into one scale: a 900 USD
// rent is not a bigger win than a 500 USD fee. So this returns the quantity AND its `kind`,
// and the sort orders by kind FIRST (every known fee above every price-only deal) and by
// value within each kind. That gives "bigger first" everywhere without ever letting a deal
// size outrank a real commission.
//
// It is also what the card chip already shows: coopTermsLabel prints the fee when there is
// one and falls back to the price when there isn't — so the feed's order now matches the
// number the agent is reading on the card, which is the actual complaint.
export const DEAL_VALUE_KINDS = ['commission', 'price']
export function dealValueFromRow(row) {
  if (!row) return null
  const c = commissionAmountFromRow(row)
  if (c) return { ...c, kind: 'commission' }
  const p = typeof row.min_price === 'string' ? parseFloat(row.min_price) : row.min_price
  if (typeof p === 'number' && isFinite(p) && p > 0) {
    return { amount: p, currency: row.min_price_currency || 'USD', kind: 'price' }
  }
  return null
}

// What to RANK this cooperation by.
//
// ⚠️ PARENT FIRST, participants only as a fallback — and this is the whole point of the
// function (fixed 2026-07-27 after the owner reported the sort looked random). The parent's
// term columns are the gateway-maintained ROLLED-UP effective view, and they are exactly what
// the card chip prints (CooperationChips.coopTermsLabel reads c.commission_percent /
// c.min_price). Ranking on anything else makes the feed order disagree with the number the
// agent is reading on the card, which is indistinguishable from "not sorted at all".
//
// The earlier version took the MAX across the parent AND every participant. Two things were
// wrong with that. It ranked on a number that is never displayed; and because participants
// hold PER-AGENT negotiated terms, one agent's outlier dominated the whole company ranking —
// live example: a 700 USD/mo rental whose parent says 700 had a participant row reading 7650,
// so it sorted above every genuine deal in the book while its card showed 700.
//
// Participants are still the fallback, because they have to be: core_visibility strips the
// parent's numbers for a viewer who is not entitled to the rollup but KEEPS that viewer's own
// participant row. For a plain agent that own-row is the only number they may see, and it is
// the right one for them to be ranked by.
export function bestVisibleDealValue(coop) {
  if (!coop) return null
  const fromParent = dealValueFromRow(coop)
  if (fromParent) return fromParent
  let best = null
  const rank = v => (v.kind === 'commission' ? 0 : 1)
  for (const p of coop.participants || []) {
    if (p.left_at) continue
    const v = dealValueFromRow(p)
    if (!v) continue
    if (!best || rank(v) < rank(best) || (rank(v) === rank(best) && v.amount > best.amount)) best = v
  }
  return best
}
